import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_ROUTES, BASE_URL } from '../../config/api-routes';
import { getUserFacingError } from '../../utils/userFacingError';
import logo from '../../images/SKIP.svg';
import './ReferenceVerification.css';

/** Public client — referees are often logged out; avoid auth redirect side effects. */
const publicApi = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

type VerificationPayload = {
  token: string;
  referenceId: string;
  recommend_hire: boolean;
  worked_together: boolean;
  relationship_summary: string;
  strengths: string;
  concerns: string;
};

type ReferenceVerificationDetails = {
  reference?: {
    name?: string;
    role?: string;
    company?: string;
  };
  candidate?: {
    name?: string;
    role_title?: string;
    username?: string;
  };
  already_verified?: boolean;
};

const ReferenceVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const legacyReferenceId = useMemo(
    () => searchParams.get('referenceId')?.trim() ?? '',
    [searchParams]
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [details, setDetails] = useState<ReferenceVerificationDetails | null>(null);

  const [form, setForm] = useState<VerificationPayload>({
    token: '',
    referenceId: '',
    recommend_hire: true,
    worked_together: true,
    relationship_summary: '',
    strengths: '',
    concerns: '',
  });

  useEffect(() => {
    setForm((prev) => ({ ...prev, token, referenceId: legacyReferenceId }));
  }, [token, legacyReferenceId]);

  useEffect(() => {
    async function load() {
      if (!token && !legacyReferenceId) {
        setError('Missing verification link. Open the link from your email.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const query = token
          ? `token=${encodeURIComponent(token)}`
          : `referenceId=${encodeURIComponent(legacyReferenceId)}`;
        const { data } = await publicApi.get(`${API_ROUTES.REFERENCE.VERIFY_REFERENCE}?${query}`);
        setDetails(data);
      } catch (e: unknown) {
        const msg = getUserFacingError(
          e,
          'We could not open this verification link. It may be expired or invalid.'
        );
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [token, legacyReferenceId]);

  const submit = async () => {
    if (!form.token && !form.referenceId) return;
    const relationship = form.relationship_summary.trim();
    const strengths = form.strengths.trim();
    if (!relationship || !strengths) {
      const msg = 'Please share how you know the candidate and their strengths.';
      setError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, unknown> = {
        relationship_summary: relationship,
        strengths,
        concerns: form.concerns.trim(),
        worked_together: form.worked_together,
        recommend_hire: form.recommend_hire,
      };
      if (form.token) {
        body.token = form.token;
      } else {
        body.referenceId = form.referenceId;
      }

      const { data } = await publicApi.post(API_ROUTES.REFERENCE.VERIFY_REFERENCE, body);
      const detail =
        (typeof data?.detail === 'string' && data.detail) || 'Reference verified successfully.';
      setSuccess(detail);
      setDetails((prev) => (prev ? { ...prev, already_verified: true } : prev));
      toast.success(detail);
    } catch (e: unknown) {
      const msg = getUserFacingError(e, 'We could not submit your feedback. Please try again.');
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="ref-verify-page">
      <section className="ref-verify-panel">
        <img src={logo} alt="SkipScreen" className="ref-verify-logo" width={140} />
        <div className="ref-verify-kicker">
          <span className="ref-verify-kicker-dot" aria-hidden />
          Reference Verification
        </div>

        {loading ? <div className="ref-verify-loading">Loading reference request…</div> : null}

        {!loading && error && !details ? (
          <div className="ref-verify-alert ref-verify-alert--error">{error}</div>
        ) : null}

        {!loading && details ? (
          <>
            <h1 className="ref-verify-title">
              Share feedback for {details.candidate?.name ?? 'this candidate'}
            </h1>
            <p className="ref-verify-sub">
              Candidate role: {details.candidate?.role_title || 'Not specified'}
            </p>
            {details.reference?.name ? (
              <p className="ref-verify-sub">
                You were listed as: {details.reference.name}
                {details.reference.role ? ` · ${details.reference.role}` : ''}
                {details.reference.company ? ` at ${details.reference.company}` : ''}
              </p>
            ) : null}

            {details.already_verified ? (
              <div className="ref-verify-alert ref-verify-alert--success">
                This reference has already been verified. Thank you!
              </div>
            ) : (
              <div className="ref-verify-form">
                <label className="ref-verify-field">
                  <span className="ref-verify-field-label">How do you know this candidate?</span>
                  <textarea
                    className="ref-verify-textarea"
                    value={form.relationship_summary}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        relationship_summary: e.target.value,
                      }))
                    }
                    placeholder="We worked together on…"
                    rows={4}
                  />
                </label>

                <label className="ref-verify-field">
                  <span className="ref-verify-field-label">Candidate strengths</span>
                  <textarea
                    className="ref-verify-textarea"
                    value={form.strengths}
                    onChange={(e) => setForm((s) => ({ ...s, strengths: e.target.value }))}
                    placeholder="Communication, leadership, technical depth…"
                    rows={4}
                  />
                </label>

                <label className="ref-verify-field">
                  <span className="ref-verify-field-label">Any concerns? (optional)</span>
                  <textarea
                    className="ref-verify-textarea"
                    value={form.concerns}
                    onChange={(e) => setForm((s) => ({ ...s, concerns: e.target.value }))}
                    placeholder="Share anything a hiring team should know"
                    rows={3}
                  />
                </label>

                <div className="ref-verify-check-grid">
                  <label className="ref-verify-check-card">
                    <input
                      type="checkbox"
                      checked={form.worked_together}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          worked_together: e.target.checked,
                        }))
                      }
                    />
                    <span>I have directly worked with this candidate</span>
                  </label>

                  <label className="ref-verify-check-card">
                    <input
                      type="checkbox"
                      checked={form.recommend_hire}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          recommend_hire: e.target.checked,
                        }))
                      }
                    />
                    <span>I would recommend this candidate</span>
                  </label>
                </div>

                {success ? (
                  <div className="ref-verify-alert ref-verify-alert--success">{success}</div>
                ) : null}

                {error ? (
                  <div className="ref-verify-alert ref-verify-alert--error">{error}</div>
                ) : null}

                <div className="ref-verify-actions">
                  <button
                    type="button"
                    className="ref-verify-submit"
                    disabled={submitting}
                    onClick={() => void submit()}
                  >
                    {submitting ? 'Submitting…' : 'Submit verification'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : null}
      </section>
    </main>
  );
};

export default ReferenceVerification;
